import React, { useState, useRef } from 'react';
import {
  View,
  TextInput,
  ScrollView,
  TouchableOpacity,
  StyleSheet,
  TextInputProps,
  NativeSyntheticEvent,
  TextInputSelectionChangeEventData,
} from 'react-native';
import { useTheme } from '../../theme/ThemeContext';
import ThemedText from './ThemedText';
import { Ionicons } from '@expo/vector-icons';

export interface MentionUser {
  id: string;
  name: string;
  profession?: string;
  photo?: string;
}

interface MentionInputProps extends Omit<TextInputProps, 'onChangeText'> {
  value: string;
  onChangeText: (text: string, mentionedUserIds: string[]) => void;
  users: MentionUser[];
  placeholder?: string;
  maxLength?: number;
  multiline?: boolean;
  numberOfLines?: number;
}

/**
 * MentionInput - A text input that supports @mentions
 * 
 * Text format: "Hello @[John Doe](user123) how are you?"
 * Display format: "Hello @John Doe how are you?"
 */
const MentionInput: React.FC<MentionInputProps> = ({
  value,
  onChangeText,
  users,
  placeholder = 'Write something...',
  maxLength,
  multiline = true,
  numberOfLines = 4,
  ...props
}) => {
  const { theme } = useTheme();
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredUsers, setFilteredUsers] = useState<MentionUser[]>([]);
  const [cursorPosition, setCursorPosition] = useState(0);
  const textInputRef = useRef<TextInput>(null);

  // Extract mentioned user IDs from text
  const extractMentionedUserIds = (text: string): string[] => {
    const mentionRegex = /@\[([^\]]+)\]\(([^)]+)\)/g;
    const userIds: string[] = [];
    let match;

    while ((match = mentionRegex.exec(text)) !== null) {
      userIds.push(match[2]); // Extract user ID
    }

    return userIds;
  };

  // Convert internal format to display format
  const getDisplayText = (text: string): string => {
    return text.replace(/@\[([^\]]+)\]\([^)]+\)/g, '@$1');
  };

  // Handle text changes
  const handleTextChange = (newText: string) => {
    // Check if user is typing @ for mentions
    const textBeforeCursor = newText.substring(0, cursorPosition);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    
    if (lastAtIndex !== -1) {
      const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
      
      // Check if there's a space/newline after @ (which closes mention mode)
      if (!/\s/.test(textAfterAt)) {
        const query = textAfterAt.toLowerCase();
        
        // Filter users based on query
        const filtered = users.filter(user =>
          user.name.toLowerCase().includes(query)
        );
        
        setFilteredUsers(filtered);
        setShowSuggestions(filtered.length > 0 && query.length >= 0);
      } else {
        setShowSuggestions(false);
      }
    } else {
      setShowSuggestions(false);
    }

    // Extract mentioned user IDs
    const mentionedUserIds = extractMentionedUserIds(value);
    onChangeText(newText, mentionedUserIds);
  };

  // Handle user selection
  const handleUserSelect = (user: MentionUser) => {
    const textBeforeCursor = value.substring(0, cursorPosition);
    const textAfterCursor = value.substring(cursorPosition);
    
    // Find the last @ before cursor
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    
    if (lastAtIndex !== -1) {
      // Build new text with mention
      const beforeMention = value.substring(0, lastAtIndex);
      const mentionText = `@[${user.name}](${user.id})`;
      const newText = beforeMention + mentionText + ' ' + textAfterCursor;
      
      // Close suggestions
      setShowSuggestions(false);
      
      // Update text and mentions
      const mentionedUserIds = extractMentionedUserIds(newText);
      onChangeText(newText, mentionedUserIds);
      
      // Move cursor after mention
      const newCursorPos = (beforeMention + mentionText + ' ').length;
      setTimeout(() => {
        setCursorPosition(newCursorPos);
        textInputRef.current?.setNativeProps({
          selection: { start: newCursorPos, end: newCursorPos },
        });
      }, 50);
    }
  };

  // Track cursor position
  const handleSelectionChange = (
    event: NativeSyntheticEvent<TextInputSelectionChangeEventData>
  ) => {
    setCursorPosition(event.nativeEvent.selection.start);
  };

  return (
    <View style={styles.container}>
      <TextInput
        {...props}
        ref={textInputRef}
        style={[
          styles.input,
          {
            color: theme.text,
          },
          props.style,
        ]}
        value={getDisplayText(value)}
        onChangeText={handleTextChange}
        onSelectionChange={handleSelectionChange}
        placeholder={placeholder}
        placeholderTextColor={theme.textSecondary}
        multiline={multiline}
        numberOfLines={numberOfLines}
        maxLength={maxLength}
        textAlignVertical="top"
      />

      {/* Suggestions Dropdown */}
      {showSuggestions && filteredUsers.length > 0 && (
        <View
          style={[
            styles.suggestionsContainer,
            {
              backgroundColor: theme.card,
              borderColor: theme.border,
            },
          ]}
        >
          <ScrollView
            keyboardShouldPersistTaps="handled"
            nestedScrollEnabled={true}
          >
            {filteredUsers.slice(0, 5).map((item, index) => (
              <TouchableOpacity
                key={item.id}
                style={[
                  styles.suggestionItem,
                  { borderBottomColor: theme.borderLight },
                  index === filteredUsers.slice(0, 5).length - 1 && { borderBottomWidth: 0 },
                ]}
                onPress={() => handleUserSelect(item)}
                activeOpacity={0.7}
              >
                <View style={[styles.avatar, { backgroundColor: theme.border }]}>
                  <Ionicons name="person" size={20} color={theme.textSecondary} />
                </View>
                <View style={styles.userInfo}>
                  <ThemedText style={[styles.userName, { color: theme.text }]}>
                    {item.name}
                  </ThemedText>
                  {item.profession && (
                    <ThemedText
                      style={[styles.userProfession, { color: theme.textSecondary }]}
                      numberOfLines={1}
                    >
                      {item.profession}
                    </ThemedText>
                  )}
                </View>
              </TouchableOpacity>
            ))}
          </ScrollView>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: 'relative',
    zIndex: 1,
  },
  input: {
    fontSize: 16,
    lineHeight: 24,
    paddingHorizontal: 16,
    paddingVertical: 16,
    minHeight: 120,
    borderWidth: 0,
  },
  suggestionsContainer: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    borderWidth: 1,
    borderRadius: 8,
    marginTop: 4,
    maxHeight: 200,
    zIndex: 1000,
    elevation: 5,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
  },
  suggestionItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    borderBottomWidth: 1,
  },
  avatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  userInfo: {
    flex: 1,
  },
  userName: {
    fontSize: 16,
    fontWeight: '500',
  },
  userProfession: {
    fontSize: 13,
    marginTop: 2,
  },
});

export default MentionInput;
